import { useQuery } from '@apollo/client';

import TacoNamespace from '../queries/TacoNamespace.graphql';
import type { TacoNamespaceQuery, TacoNamespaceQueryVariables } from '../queries/__generated__/TacoNamespace';
import type { TacoConfig } from '../types/taco-types';

type TacoParameter<E> = {
  data?: E extends TacoConfig ? E : never;
  error?: string;
  loading: boolean;
};

/**
 * Use this React hook to get the value of a TACO configuration parameter.
 * Takes the namespace and the parameter name.
 *
 * @param {string} namespace - The namespace of the parameter.
 *
 * @param {string} parameter - The parameter name you want the value of.
 *
 * @returns {TacoParameter} An object containing the value of the parameter, along
 *   with some additional data:
 *     - `loading` - Whether the parameter is still loading.
 *     - `data` - The value of the parameter.
 *     - `error` - An error message if the parameter is not found or another error
 *       occurred while fetching the configuration.
 *
 * Example usage:
 *
 * ```tsx
 * const { data, loading, error } = useTacoConfigParameter('my-namespace', 'my-parameter');
 * ```
 */
const useTacoConfigParameter = <S extends TacoConfig>(namespace: string, parameter: string): TacoParameter<S> => {
  const { data, loading, error } = useQuery<TacoNamespaceQuery, TacoNamespaceQueryVariables>(TacoNamespace, {
    context: {
      clientName: 'gatewayGql',
    },
    variables: { namespace },
  });

  if (loading) {
    return {
      loading: true,
    };
  }
  if (error) {
    return {
      error: error.message,
      loading: false,
    };
  }
  const namespaceData = data?.TacoConfig?.queryByNamespace?.values;
  if (!namespaceData) {
    return {
      error: 'An unexpected error occurred while fetching the configuration.',
      loading: false,
    };
  }
  const parameterValue = namespaceData.find((p) => p.parameter === parameter);
  if (!parameterValue) {
    return {
      error: `Parameter ${parameter} not found in namespace ${namespace}.`,
      loading: false,
    };
  }
  return {
    data: parameterValue.value,
    loading: false,
  };
};

export default useTacoConfigParameter;
